import { css } from '@emotion/core';
import { Tag } from 'antd';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Icon, TranslationProgress } from '@/components';
import { PROJECT_PERMISSION, PROJECT_STATUS } from '@/constants';
import { FC, Project } from '@/interfaces';
import { resetFilesState } from '@/store/file/slice';
import style from '@/style';
import { cardActiveEffect, cardClickEffect, clickEffect } from '@/utils/style';
import { can } from '@/utils/user';
import { getProjectModeColor, getProjectModeLabel } from '@/utils/project';

/** 项目列表元素的属性接口 */
interface ProjectItemProps {
  from: 'team' | 'user';
  project: Project | Project;
  className?: string;
}
/**
 * 项目列表元素
 */
export const ProjectItem: FC<ProjectItemProps> = ({
  from,
  project,
  className,
}) => {
  const { formatMessage } = useIntl(); // i18n
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const archived =
    project.status === PROJECT_STATUS.ARCHIVED || !!project.archived;
  // 嵌字（已导出）绿条：已嵌字图片数 / 图片总数
  const imageFileCount = project.imageFileCount ?? 0;
  const typesetPercent =
    imageFileCount > 0
      ? Math.min(100, ((project.typesetFileCount ?? 0) / imageFileCount) * 100)
      : 0;

  // 点击后跳转路径前缀
  let urlPrefix = '';
  if (from === 'team') {
    urlPrefix = '/projects';
  }

  const handleClick = () => {
    dispatch(resetFilesState());
    if (project.status === PROJECT_STATUS.FINISHED && !archived) {
      history.push(`${url + urlPrefix}/${project.id}/setting`);
    } else {
      if (can(project, PROJECT_PERMISSION.ACCESS)) {
        history.push(`${url + urlPrefix}/${project.id}`);
      } else {
        history.push(`${url + urlPrefix}/${project.id}/preview`);
      }
    }
  };

  const handleSettingClick = () => {
    history.push(`${url + urlPrefix}/${project.id}/setting`);
  };

  return (
    <div
      className={classNames('ProjectItem', className, {
        'ProjectItem--hasBelong': from === 'user',
        'ProjectItem--active': location.pathname.includes(
          'projects/' + project.id,
        ),
      })}
      css={css`
        width: 100%;
        border-radius: ${style.borderRadiusBase};
        overflow: hidden;
        transition:
          box-shadow 100ms,
          border-color 100ms;
        border: 1px solid ${style.borderColorLight};
        padding: 3px ${style.paddingBase - 5}px 0;
        &.ProjectItem--hasBelong {
          padding-top: ${style.paddingBase - 3}px;
        }
        &.ProjectItem--active {
          ${cardActiveEffect()};
        }
        .ProjectItem__Belong {
          width: 100%;
          display: flex;
          align-items: center;
          font-size: 12px;
          line-height: 12px;
          color: ${style.textColorSecondary};
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ProjectItem__TeamName {
          flex: none;
          max-width: 31%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ProjectItem__TeamToProjectSetIcon {
          flex: none;
          margin: 0 4px;
        }
        .ProjectItem__ProjectSetName {
          flex: none;
          max-width: calc(69% - 14px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ProjectItem__Name {
          display: flex;
          align-items: flex-start;
          gap: 4px 6px;
          max-height: 112px;
          overflow: hidden;
          box-sizing: content-box;
          margin: 5px 0;
        }
        .ProjectItem__NameText {
          flex: 0 1 auto;
          min-width: 0;
          max-width: max-content;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          overflow: hidden;
          word-break: break-all;
          font-size: 14px;
          line-height: 20px;
        }
        .ProjectItem__NameTags {
          flex: none;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px;
          max-width: 100%;
          min-height: 20px;
          white-space: nowrap;
        }
        .ProjectItem__NameTags .ant-tag {
          flex: none;
          margin: 0;
          font-weight: normal;
          line-height: 18px;
          white-space: nowrap;
        }
        @media (max-width: 480px) {
          /* 窄屏：名称占满可用宽度；标签放不下时整体换到下一行，避免被裁切 */
          .ProjectItem__Name {
            flex-wrap: wrap;
          }
          .ProjectItem__NameText {
            flex: 1 1 120px;
          }
        }
        .ProjectItem__Bottom {
          display: flex;
          align-items: center;
          height: 32px;
        }
        .ProjectItem__BottomButtonWrapper {
          margin-left: auto;
          font-size: 12px;
          display: flex;
          align-items: center;
          color: ${style.textColorSecondaryLighter};
        }
        .ProjectItem__BottomButtonSetting {
          flex: none;
          border-radius: ${style.borderRadiusBase};
          padding: 5px 9px;
          margin-right: -8px;
          ${clickEffect()};
        }
        .ProjectItem__BottomButtonIcon {
          margin-right: 3px;
        }
        .ProjectItem__BottomButtonFinished {
          .ProjectItem__BottomButtonIcon {
            margin-right: 0;
          }
        }
        ${cardClickEffect()};
      `}
      onClick={handleClick}
    >
      {from === 'user' && (
        <div className="ProjectItem__Belong">
          <div className="ProjectItem__TeamName">{project.team.name}</div>
          <Icon
            className="ProjectItem__TeamToProjectSetIcon"
            icon="angle-right"
          />
          <div className="ProjectItem__ProjectSetName">
            {project.projectSet.default
              ? formatMessage({ id: 'projectSet.default' })
              : project.projectSet.name}
          </div>
        </div>
      )}
      <div className="ProjectItem__Name">
        <span className="ProjectItem__NameText" title={project.name}>
          {project.name}
        </span>
        <span className="ProjectItem__NameTags">
          <Tag
            className="ProjectItem__ModeTag"
            color={getProjectModeColor(
              project.withProofread !== false,
              project.withAuthor === true,
            )}
          >
            {getProjectModeLabel(
              formatMessage,
              project.withProofread !== false,
              project.withAuthor === true,
            )}
          </Tag>
          {archived && (
            <Tag className="ProjectItem__ArchivedTag">
              {formatMessage({ id: 'project.archived' })}
            </Tag>
          )}
        </span>
      </div>
      <TranslationProgress
        className="ProjectItem__TranslationProgressLine"
        sourceCount={project.sourceCount * project.targetCount}
        translatedSourceCount={project.translatedSourceCount}
        checkedSourceCount={project.checkedSourceCount}
        typesetPercent={typesetPercent}
        hideChecked={project.withProofread === false}
        type="line"
      />
      <div className="ProjectItem__Bottom">
        <TranslationProgress
          className="ProjectItem__TranslationProgressText"
          sourceCount={project.sourceCount * project.targetCount}
          translatedSourceCount={project.translatedSourceCount}
          checkedSourceCount={project.checkedSourceCount}
          hideChecked={project.withProofread === false}
          type="text"
        />
        <div className="ProjectItem__BottomButtonWrapper">
          {project.status === PROJECT_STATUS.FINISHED && !archived ? (
            <div className="ProjectItem__BottomButton ProjectItem__BottomButtonFinished">
              <Icon
                className="ProjectItem__BottomButtonIcon"
                icon={['far', 'kiss-wink-heart']}
              />{' '}
              {formatMessage({ id: 'project.finished' })}
            </div>
          ) : can(project, PROJECT_PERMISSION.ACCESS) ? (
            <div
              className="ProjectItem__BottomButton ProjectItem__BottomButtonSetting"
              onClick={(e) => {
                e.stopPropagation();
                handleSettingClick();
              }}
            >
              <Icon className="ProjectItem__BottomButtonIcon" icon="cog" />
              {formatMessage({ id: 'site.setting' })}
            </div>
          ) : (
            <div className="ProjectItem__BottomButton">
              <Icon className="ProjectItem__BottomButtonIcon" icon="plus" />
              {formatMessage({ id: 'site.join' })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
